// ws-client.ts
//
// 以最小依赖实现的浏览器/Node/Bun 通用 WebSocket 客户端：
// - 支持基于 JSON 的 Bus 消息格式（与服务端 rx-ws-bus.ts 对齐）
// - 支持自动重连（指数退避 + 抖动）
// - 仅使用 Bearer Token 进行鉴权：
//   - Node/Bun（使用 'ws'）：通过 Authorization 头发送
//   - 浏览器：通过 WebSocket 子协议携带（协议形如 bearer.<token>）
//
// 说明：
// - 服务端只要求消息为 { type: string, topic?: string, payload?: any, ... }
// - 为兼容浏览器握手限制，服务端需从 Sec-WebSocket-Protocol 中解析 bearer.<token>
//

export type BusMessage<T = unknown> = {
  type: string;
  topic?: string;
  payload?: T;
  [key: string]: unknown;
};

export type WebSocketClientOptions = {
  url: string;
  protocols?: string | string[];
  // 鉴权
  token?: string;
  extraHeaders?: Record<string, string>; // 仅 Node 'ws' 可用
  // 重连
  autoReconnect?: boolean;
  reconnectMinDelayMs?: number; // 初始延时
  reconnectMaxDelayMs?: number; // 最大延时
  maxRetries?: number | null; // null 为无限
  // 其他
  debug?: boolean;
};

type ClosePayload = { code?: number; reason?: string };

type EventMap = {
  open: void;
  connecting: void;
  close: ClosePayload;
  error: unknown;
  message: BusMessage | string; // 字符串消息会原样抛出
  reconnect: { attempt: number; delayMs: number };
  // 动态：消息类型事件（例如 "type:direct"）
  [dynamic: `type:${string}`]: BusMessage;
};

type Listener<T> = (payload: T) => void;

class TinyEmitter {
  private handlers = new Map<string, Set<Listener<any>>>();

  on<T extends keyof EventMap>(evt: T, fn: Listener<EventMap[T]>) {
    const key = String(evt);
    if (!this.handlers.has(key)) this.handlers.set(key, new Set());
    this.handlers.get(key)!.add(fn);
    return () => this.off(evt, fn);
  }

  off<T extends keyof EventMap>(evt: T, fn: Listener<EventMap[T]>) {
    const key = String(evt);
    this.handlers.get(key)?.delete(fn);
  }

  emit<T extends keyof EventMap>(evt: T, payload: EventMap[T]) {
    const key = String(evt);
    const set = this.handlers.get(key);
    if (!set || set.size === 0) return;
    for (const fn of Array.from(set)) {
      try {
        fn(payload);
      } catch {}
    }
  }
}

function hasGlobalWebSocket(): boolean {
  return typeof globalThis !== "undefined" && "WebSocket" in globalThis;
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

type WebSocketLike = {
  readyState: number;
  send: (data: string) => void;
  close: (code?: number, reason?: string) => void;
};

// WebSocket 状态常量 (标准 WebSocket readyState 值)
const WS_STATE = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3,
} as const;

// 消息类型常量
const MESSAGE_TYPE = {
  DIRECT: "direct",
} as const;

// 其他常量
const NON_TEXT_MESSAGE_PLACEHOLDER = "[[non-text message]]";
const UTF8_ENCODING = "utf-8";

export class WebSocketBusClient {
  private readonly options: Required<
    Pick<
      WebSocketClientOptions,
      | "url"
      | "protocols"
      | "token"
      | "extraHeaders"
      | "autoReconnect"
      | "reconnectMinDelayMs"
      | "reconnectMaxDelayMs"
      | "maxRetries"
      | "debug"
    >
  >;

  private emitter = new TinyEmitter();
  private ws: (WebSocketLike & any) | null = null;
  private manuallyClosed = false;
  private connecting = false;
  private connectPromise: Promise<void> | null = null;
  private reconnectAttempt = 0;
  private pendingQueue: string[] = [];
  private textDecoder = new TextDecoder("utf-8");

  constructor(opts: WebSocketClientOptions) {
    this.options = {
      url: opts.url,
      protocols: opts.protocols ?? [],
      token: opts.token ?? "",
      extraHeaders: opts.extraHeaders ?? {},
      autoReconnect: opts.autoReconnect ?? true,
      reconnectMinDelayMs: opts.reconnectMinDelayMs ?? 500,
      reconnectMaxDelayMs: opts.reconnectMaxDelayMs ?? 8000,
      maxRetries: opts.maxRetries ?? null,
      debug: opts.debug ?? false,
    };
  }

  on<T extends keyof EventMap>(evt: T, listener: Listener<EventMap[T]>) {
    return this.emitter.on(evt, listener);
  }

  off<T extends keyof EventMap>(evt: T, listener: Listener<EventMap[T]>) {
    this.emitter.off(evt, listener);
  }

  onType(type: string, listener: Listener<BusMessage>) {
    // 动态类型事件的类型断言
    return this.on(`type:${type}` as keyof EventMap, listener as Listener<EventMap[keyof EventMap]>);
  }

  async connect(): Promise<void> {
    if (this.connecting || this.ws?.readyState === WS_STATE.OPEN) {
      return this.connectPromise ?? Promise.resolve();
    }

    this.manuallyClosed = false;
    this.connecting = true;
    this.emitter.emit("connecting", undefined as unknown as void);

    this.connectPromise = this._open();
    await this.connectPromise;
  }

  disconnect(code?: number, reason?: string) {
    this.manuallyClosed = true;
    if (this.ws) {
      try {
        this.ws.close(code, reason);
      } catch {}
    }
  }

  send(msg: BusMessage) {
    const text = JSON.stringify(msg);
    if (this.ws && this.ws.readyState === WS_STATE.OPEN) {
      try {
        this.ws.send(text);
      } catch {
        this.pendingQueue.push(text);
      }
      return;
    }
    this.pendingQueue.push(text);
  }

  sendType(type: string, payload?: unknown, extras?: Record<string, unknown>) {
    const base: BusMessage = { type, payload };
    this.send(extras ? Object.assign(base, extras) : base);
  }

  sendDirect(to: string, payload: Record<string, unknown> | unknown[], id?: string) {
    if (payload == null || typeof payload !== "object") {
      throw new TypeError("direct payload must be a JSON object or array");
    }
    const msg: BusMessage = { type: MESSAGE_TYPE.DIRECT, to, payload };
    if (typeof id === "string" && id.length > 0) {
      msg.id = id;
    }
    this.send(msg);
  }

  private async _open(): Promise<void> {
    const { token, protocols, debug } = this.options;

    // 计算子协议（浏览器用 bearer.<token> 传递凭证）
    const baseProtocols =
      typeof protocols === "string"
        ? protocols
          ? [protocols]
          : []
        : Array.isArray(protocols)
        ? protocols.slice()
        : [];
    const computedProtocols = baseProtocols.slice();
    if (token) {
      computedProtocols.push(`bearer.${token}`);
    }
    // 去重
    const protoSet = new Set(computedProtocols.filter(Boolean));
    const finalProtocols = Array.from(protoSet);

    // 选择实现：优先全局 WebSocket，否则动态加载 'ws'
    let useNodeWs = false;
    if (!hasGlobalWebSocket()) {
      useNodeWs = true;
    }

    if (useNodeWs) {
      // @ts-ignore 可选依赖：在 Node/Bun 环境才需要 'ws'，浏览器无需解析此模块
      const { default: NodeWS } = await import("ws").catch(() => ({ default: null }));
      if (!NodeWS) {
        throw new Error(
          "当前运行时无全局 WebSocket，请安装可选依赖 'ws' 以在 Node/Bun 环境使用，或在浏览器中使用。"
        );
      }

      const headers: Record<string, string> = { ...this.options.extraHeaders };
      if (token) {
        headers["authorization"] = `Bearer ${token}`;
      }

      // NodeWS 构造函数需要这个断言，因为是动态导入
      const ws = new (NodeWS as any)(
        this.options.url,
        finalProtocols.length > 0 ? finalProtocols : undefined,
        { headers }
      );
      this.attachHandlers(ws, /*isNodeWs*/ true);
      this.ws = ws;
      return;
    }

    // 浏览器 / Bun 的标准 WebSocket
    // globalThis.WebSocket 需要这个断言，因为在类型定义中可能不存在
    const WSImpl = (globalThis as any).WebSocket as any;
    const ws =
      finalProtocols.length > 0
        ? new WSImpl(this.options.url, finalProtocols)
        : new WSImpl(this.options.url);
    this.attachHandlers(ws, /*isNodeWs*/ false);
    this.ws = ws;
    if (debug) {
      // no-op: 保留占位，方便未来扩展日志
    }
  }

  // 辅助方法：清空待发送队列
  private flushPendingQueue(ws: any): void {
    if (this.pendingQueue.length === 0) return;

    const queue = this.pendingQueue;
    this.pendingQueue = [];
    let failedIndex = -1;

    for (let i = 0; i < queue.length; i++) {
      try {
        ws.send(queue[i]);
      } catch {
        failedIndex = i;
        break;
      }
    }

    // 如果有消息发送失败，将剩余消息放回队列
    if (failedIndex >= 0) {
      this.pendingQueue = queue.slice(failedIndex);
    }
  }

  // 辅助方法：解析 WebSocket 消息数据
  private parseMessageData(evt: any, isNodeWs: boolean): string | null {
    if (typeof evt?.data === "string") {
      return evt.data;
    }

    if (evt?.data && (evt.data instanceof ArrayBuffer || ArrayBuffer.isView(evt.data))) {
      const arr = evt.data instanceof ArrayBuffer ? new Uint8Array(evt.data) : (evt.data as Uint8Array);
      return this.textDecoder.decode(arr);
    }

    if (isNodeWs && typeof evt?.data === "undefined" && typeof evt === "object" && "toString" in evt) {
      // 'ws' 的 message 事件参数为 (data: RawData)，可能是 Buffer
      // @ts-ignore - Buffer 类型在浏览器环境不存在，但这段代码只在 Node 环境运行
      const raw = evt as unknown as Buffer;
      return raw.toString(UTF8_ENCODING);
    }

    return null;
  }

  // 辅助方法：处理文本消息
  private handleTextMessage(text: string): void {
    try {
      const obj = JSON.parse(text) as BusMessage;
      if (obj && typeof obj === "object" && typeof obj.type === "string") {
        this.emitter.emit("message", obj);
        this.emitter.emit(`type:${obj.type}` as keyof EventMap, obj);
      } else {
        this.emitter.emit("message", text);
      }
    } catch {
      this.emitter.emit("message", text);
    }
  }

  private attachHandlers(ws: any, isNodeWs: boolean) {
    const add =
      typeof ws.addEventListener === "function"
        ? (evt: string, fn: any) => ws.addEventListener(evt, fn)
        : typeof ws.on === "function"
        ? (evt: string, fn: any) => ws.on(evt, fn)
        : null;

    if (!add) throw new Error("未知的 WebSocket 实现，缺少事件订阅能力");

    add("open", () => {
      this.connecting = false;
      this.reconnectAttempt = 0;
      // flush pending messages
      this.flushPendingQueue(ws);
      this.emitter.emit("open", undefined as unknown as void);
    });

    add("message", (evt: any) => {
      const text = this.parseMessageData(evt, isNodeWs);
      if (text == null) {
        this.emitter.emit("message", NON_TEXT_MESSAGE_PLACEHOLDER);
        return;
      }
      this.handleTextMessage(text);
    });

    add("error", (err: unknown) => {
      this.emitter.emit("error", err);
    });

    add("close", (evt: any) => {
      const code: number | undefined = evt?.code;
      const reason: string | undefined = typeof evt?.reason === "string" ? evt.reason : undefined;
      this.emitter.emit("close", { code, reason });
      this.ws = null;
      this.handleReconnect();
    });
  }

  private async handleReconnect() {
    if (this.manuallyClosed || !this.options.autoReconnect) return;
    if (this.options.maxRetries !== null && this.reconnectAttempt >= this.options.maxRetries) return;

    const base = Math.min(
      this.options.reconnectMinDelayMs * Math.pow(2, this.reconnectAttempt),
      this.options.reconnectMaxDelayMs
    );
    const jitter = Math.floor(base * 0.2 * Math.random());
    const delay = base + jitter;
    this.reconnectAttempt += 1;
    this.emitter.emit("reconnect", { attempt: this.reconnectAttempt, delayMs: delay });

    await sleep(delay);
    if (this.manuallyClosed) return;
    try {
      await this.connect();
    } catch {
      // 失败则继续下一轮
      this.handleReconnect();
    }
  }
}

export function createClient(options: WebSocketClientOptions) {
  return new WebSocketBusClient(options);
}

export type { WebSocketClientOptions as WsClientOptions };


